import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
export function JavaScriptLibsPage() {
  return <DocPageLayout title="JavaScript Libraries" description="Client-side JavaScript libraries for integrating the payment gateway into your web application." data-id="element-1695">
      <p className="text-gray-700 mb-6" data-id="element-1696">
        The gateway provides two JavaScript libraries for client-side
        integration: <strong data-id="element-1697">Checkout.js</strong> for the hosted payment page
        and <strong data-id="element-1698">PaymentSession.js</strong> for hosted session fields. Both
        libraries handle sensitive card data securely in the browser.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1699">
        Installation
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1700">
        Include the appropriate library via a script tag. No npm package is
        required — the libraries are served directly from the gateway.
      </p>
      <CodeBlock language="HTML" title="Script Include" code={`<!-- Checkout.js - For hosted payment page -->
<script src="https://test-gateway.mastercard.com/checkout/version/72/checkout.js"
        data-error="errorCallback"
        data-cancel="cancelCallback">
</script>

<!-- PaymentSession.js - For hosted session fields -->
<script src="https://test-gateway.mastercard.com/form/version/72/merchant/{merchantId}/session.js">
</script>`} data-id="element-1701" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1702">
        Checkout.js
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1703">
        Checkout.js provides a fully hosted payment page that handles the entire
        payment flow. It opens as a lightbox overlay or redirects to a hosted
        page.
      </p>
      <CodeBlock language="JavaScript" title="Checkout.js Configuration" code={`Checkout.configure({
  session: {
    id: "SESSION0002abcdef1234567890"
  },
  interaction: {
    merchant: {
      name: "Your Store Name",
      logo: "https://your-site.com/logo.png"
    },
    displayControl: {
      billingAddress: "HIDE",
      customerEmail: "HIDE"
    }
  },
  order: {
    description: "Order #12345"
  }
});

// Show the payment page
function payNow() {
  Checkout.showPaymentPage();
}

// Or show as lightbox
function payLightbox() {
  Checkout.showLightbox();
}`} data-id="element-1704" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1705">
        PaymentSession.js
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1706">
        PaymentSession.js embeds secure hosted input fields into your custom
        payment form. You control the layout and styling while the gateway
        handles sensitive data.
      </p>
      <CodeBlock language="JavaScript" title="PaymentSession.js Configuration" code={`PaymentSession.configure({
  session: "SESSION0002abcdef1234567890",
  fields: {
    card: {
      number: "#card-number",
      securityCode: "#security-code",
      expiryMonth: "#expiry-month",
      expiryYear: "#expiry-year",
      nameOnCard: "#cardholder-name"
    }
  },
  frameEmbeddingMitigation: ["javascript"],
  callbacks: {
    initialized: function(response) {
      // Fields are ready
      console.log("Hosted fields initialized");
    },
    formSessionUpdate: function(response) {
      if (response.status === "ok") {
        console.log("Session updated:", response.session.id);
        // Submit payment from your server
      } else {
        console.error("Update failed:", response);
      }
    }
  },
  interaction: {
    displayControl: {
      formatCard: "EMBOSSED",
      invalidFieldCharacters: "REJECT"
    }
  }
});

// Call when user clicks "Pay"
document.getElementById("pay-btn").addEventListener("click", function() {
  PaymentSession.updateSessionFromForm("card");
});`} data-id="element-1707" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1708">
        Events and Callbacks
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-1709">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1710">
          <thead data-id="element-1711">
            <tr className="bg-gray-50" data-id="element-1712">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1713">
                Event
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1714">
                Library
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1715">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1716">
            <tr data-id="element-1717">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1718">initialized</td>
              <td className="px-4 py-3" data-id="element-1719">Both</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1720">
                Fired when the library is ready
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1721">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1722">formSessionUpdate</td>
              <td className="px-4 py-3" data-id="element-1723">PaymentSession</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1724">
                Fired after session update attempt
              </td>
            </tr>
            <tr data-id="element-1725">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1726">complete</td>
              <td className="px-4 py-3" data-id="element-1727">Checkout</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1728">
                Payment completed successfully
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1729">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1730">cancel</td>
              <td className="px-4 py-3" data-id="element-1731">Checkout</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1732">
                Customer cancelled the payment
              </td>
            </tr>
            <tr data-id="element-1733">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1734">error</td>
              <td className="px-4 py-3" data-id="element-1735">Both</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1736">
                An error occurred during processing
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1737">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1738">
                focusIn / focusOut
              </td>
              <td className="px-4 py-3" data-id="element-1739">PaymentSession</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1740">
                Hosted field gained/lost focus
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="info" title="Browser Compatibility" data-id="element-1741">
        Both libraries support all modern browsers including Chrome 60+, Firefox
        55+, Safari 11+, and Edge 79+. Internet Explorer is not supported.
      </InfoBox>

      <InfoBox type="tip" title="Content Security Policy" data-id="element-1742">
        If your site uses CSP headers, add the gateway domain to your{' '}
        <code className="bg-green-100 px-1 rounded text-xs font-mono" data-id="element-1743">
          script-src
        </code>{' '}
        and{' '}
        <code className="bg-green-100 px-1 rounded text-xs font-mono" data-id="element-1744">
          frame-src
        </code>{' '}
        directives to allow the hosted fields to load.
      </InfoBox>
    </DocPageLayout>;
}